import { useRef, useState } from "react";

import styles from "./Accordion.module.scss";

import ChevronDown from "@/DesignSystem/Atoms/Icons/Types/ChevronDown/chevron-down-regular.svg";

type AccordionVariantType = "primary" | "secondary" | "tertiary" | "strong";

/**
 * Interface representing the properties for the Accordion component.
 */
interface IAccordionProps {
    /**
     * The id of the accordion
     */
    id?: string;

    /*
     * The title of the accordion
     */
    title: string;

    /**
     * The size of the title
     */
    titleSize?: "label-lg" | "label-xl" | "title-md" | "title-xl";

    /**
     * The initial state of the accordion. If onToggle is provided, this will be the value the accordion will be set to.
     */
    isOpen: boolean;

    /**
     * The content of the according that will be rendered when the accordion is open.
     */
    children: React.ReactNode

    /**
     * The action buttons that will be rendered on the right side of the accordion.
     */
    actionButtons?: JSX.Element[];

    /**
     * The function that will be called when the accordion is toggled.
     * @param isOpen If the accordion is open or not.
     */
    onToggle?: (isOpen: boolean) => void;

    /**
     * The variant of the accordion.
     */
    variant?: AccordionVariantType;

    /*
     * A selector to target in frontend tests.
     */
    dataCy?: string;

    /**
     * If the accordion is in passive mode.
     */
    isPassive?: boolean;
}

function Accordion({
    id,
    isOpen = true,
    title,
    actionButtons,
    variant = "primary",
    onToggle,
    children,
    dataCy,
    titleSize = "label-lg",
    isPassive
}: Readonly<IAccordionProps>): JSX.Element {
    const [isOpenState, setIsOpenState] = useState(isOpen);

    const isOpenResult = onToggle ? isOpen : isOpenState;

    function onToggleAccordion(): void {
        onToggle ? onToggle(!isOpenResult) : setIsOpenState(!isOpenResult);
    }

    const contentRef = useRef<HTMLDivElement>(null);

    const panelId = `${id}-panel`;
    const contentId = `${id}-content`;

    return (
        <div
            className={`${styles.accordion}`}
            data-cy={dataCy}
            id={id}
        >
            <h2
                className={`${styles.header} ${styles[variant]} ${isPassive ? styles.passive : ""}`}
                onClick={() => onToggleAccordion()}
                id={panelId}
            >
                <button
                    className={`${styles.title} ${styles[titleSize]}`}
                    aria-expanded={isOpenResult}
                    aria-controls={contentId}
                >
                    {title}
                </button>
                <div className={styles.rightSideControls}>
                    <div className={styles.actionButtons}>
                        {actionButtons}
                    </div>
                    <button
                        className={`${styles.chevron} ${isOpenResult ? styles.isOpen : ""}`}
                        aria-controls={contentId}
                    >
                        <ChevronDown />
                    </button>
                </div>
            </h2>
            <section
                className={`${styles.contentContainer} ${isOpenResult ? styles.open : ""} ${styles[variant]}`}
                ref={contentRef}
                aria-labelledby={panelId}
                id={contentId}
            >
                <div className={styles.content}>
                    <div className={styles.contentPadding}>
                        {children}
                    </div>
                </div>
            </section>
        </div>
    );
}

export {
    Accordion,
    AccordionVariantType,
    IAccordionProps
};
